// File: src/components/pages/MyPayments.jsx
// Student-facing payments history — read-only view of all payment records

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthToken } from '../../hooks/useAuthToken';
import {
  Wallet,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  RefreshCw,
  Calendar,
  BookOpen
} from 'lucide-react';
import { paymentAPI } from '../../services/api';
import '../../styles/unified.css';

// ─── helpers ─────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP'
  }).format(amount);
};

const statusMeta = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'approved':  return { bg: '#dcfce7', text: '#16a34a', icon: CheckCircle2, label: 'Approved'  };
    case 'claimed':   return { bg: '#dbeafe', text: '#2563eb', icon: Clock,        label: 'Claimed'   };
    case 'rejected':  return { bg: '#fee2e2', text: '#dc2626', icon: XCircle,      label: 'Rejected'  };
    default:          return { bg: '#f1f5f9', text: '#64748b', icon: CreditCard,   label: status || 'Unknown' };
  }
};

// ─── Summary bar ─────────────────────────────────────────────────────────────

const SummaryBar = ({ payments }) => {
  const approved  = payments.filter(p => p.status?.toLowerCase() === 'adjusted');
  const totalPaid = approved.reduce((sum, p) => sum + (p.paymentAmount || 0), 0);
  const totalClasses = approved.reduce((sum, p) => sum + (p.numberOfClasses || 0), 0);

  const stat = (label, value, color) => (
    <div style={{
      flex: 1, background: '#fff', borderRadius: '0.65rem',
      padding: '0.9rem 1.1rem', border: '1px solid #e5e7eb',
      minWidth: '120px'
    }}>
      <div style={{ fontSize: '1.3rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.15rem' }}>{label}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
      {stat('Total payments', payments.length, '#111827')}
      {stat('Approved', approved.length, '#16a34a')}
      {stat('Total paid', formatCurrency(totalPaid), '#667eea')}
      {stat('Classes purchased', totalClasses || '—', '#764ba2')}
    </div>
  );
};

// ─── PaymentRow ───────────────────────────────────────────────────────────────

const PaymentRow = ({ payment }) => {
  const [hovered, setHovered] = useState(false);
  const meta = statusMeta(payment.status);
  const Icon = meta.icon;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        border: `1px solid ${hovered ? '#667eea' : '#e5e7eb'}`,
        borderRadius: '0.75rem',
        padding: '1rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap',
        boxShadow: hovered ? '0 4px 12px rgba(102,126,234,0.1)' : 'none',
        transition: 'all 0.2s'
      }}
    >
      {/* Status icon */}
      <div style={{
        width: '36px', height: '36px', borderRadius: '50%',
        background: meta.bg, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0
      }}>
        <Icon size={17} color={meta.text} />
      </div>

      {/* Amount + method */}
      <div style={{ minWidth: '110px' }}>
        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827' }}>
          {formatCurrency(payment.paymentAmount)}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'capitalize' }}>
          {payment.paymentMethod || '—'}
        </div>
      </div>

      {/* Date */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: 1, minWidth: '120px' }}>
        <Calendar size={13} color="#6b7280" />
        <span style={{ fontSize: '0.83rem', color: '#374151' }}>
          {formatDate(payment.paymentDate)}
        </span>
      </div>

      {/* Classes */}
      {payment.numberOfClasses != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <BookOpen size={13} color="#6b7280" />
          <span style={{ fontSize: '0.83rem', color: '#374151' }}>
            {payment.numberOfClasses} {payment.numberOfClasses === 1 ? 'class' : 'classes'}
          </span>
        </div>
      )}

      {/* Option / note */}
      {(payment.paymentOption || payment.notes) && (
        <div style={{
          fontSize: '0.78rem', color: '#6b7280',
          maxWidth: '180px', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {payment.paymentOption || payment.notes}
        </div>
      )}

      {/* Status pill — pushed right */}
      <span style={{
        marginLeft: 'auto',
        background: meta.bg, color: meta.text,
        fontSize: '0.7rem', fontWeight: 700,
        padding: '0.15rem 0.55rem', borderRadius: '9999px',
        flexShrink: 0
      }}>
        {meta.label}
      </span>
    </div>
  );
};

// ─── MyPayments ───────────────────────────────────────────────────────────────

const MyPayments = () => {
  const getAccessTokenSilently = useAuthToken();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await paymentAPI.getMyPayments(getAccessTokenSilently);
      if (res.success) {
        // Newest first
        const sorted = [...(res.data || [])].sort(
          (a, b) => new Date(b.paymentDate || b.createdAt) - new Date(a.paymentDate || a.createdAt)
        );
        setPayments(sorted);
      } else {
        setError('Failed to load your payments.');
      }
    } catch (err) {
      console.error('MyPayments fetch error:', err);
      setError(err.message || 'Failed to load your payments.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.75rem', paddingBottom: '1rem',
        borderBottom: '2px solid #e5e7eb', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <Wallet size={28} color="#667eea" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#111827' }}>
              My Payments
            </h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
              Payment history with your instructor
            </p>
          </div>
        </div>
        <button
          onClick={fetchPayments}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: '0.5rem', padding: '0.45rem 0.85rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            color: '#6b7280', fontSize: '0.85rem', fontWeight: 500,
            opacity: loading ? 0.6 : 1
          }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }} />
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading your payments…</p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          background: '#fee2e2', border: '1px solid #fca5a5',
          borderRadius: '0.75rem', padding: '1.25rem 1.5rem',
          color: '#dc2626', flexWrap: 'wrap'
        }}>
          <AlertCircle size={18} />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={fetchPayments} style={{
            background: '#dc2626', color: '#fff', border: 'none',
            borderRadius: '0.4rem', padding: '0.35rem 0.85rem',
            cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
          }}>Retry</button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && payments.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '3.5rem 2rem',
          background: '#f8fafc', borderRadius: '0.75rem',
          border: '1px dashed #e2e8f0'
        }}>
          <Wallet size={40} color="#d1d5db" style={{ marginBottom: '0.75rem' }} />
          <h3 style={{ margin: '0 0 0.35rem', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
            No payment records yet
          </h3>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.88rem' }}>
            Payments recorded by your instructor will appear here.
          </p>
        </div>
      )}

      {/* Summary + list */}
      {!loading && !error && payments.length > 0 && (
        <>
          <SummaryBar payments={payments} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            {payments.map(p => (
              <PaymentRow key={p.paymentId} payment={p} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MyPayments;
